import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  APPROVER_ROLES,
  createDiscountWorkflowId,
  type DiscountWorkflowFormValue,
} from "./discountWorkflows.helpers";

type DiscountWorkflowsEditorProps = {
  value: DiscountWorkflowFormValue[];
  onChange: (next: DiscountWorkflowFormValue[]) => void;
};

const ensureNumber = (input: string, fallback: number, clamp?: { min?: number; max?: number }) => {
  const parsed = Number.parseInt(input, 10);
  if (Number.isNaN(parsed)) {
    return fallback;
  }
  const min = clamp?.min ?? Number.MIN_SAFE_INTEGER;
  const max = clamp?.max ?? Number.MAX_SAFE_INTEGER;
  return Math.min(Math.max(parsed, min), max);
};

const toggleArrayValue = (current: string[], value: string): string[] =>
  current.includes(value) ? current.filter((item) => item !== value) : [...current, value];

export const DiscountWorkflowsEditor = ({ value, onChange }: DiscountWorkflowsEditorProps) => {
  const workflowCount = value.length;
  const preview = useMemo(() => JSON.stringify(value, null, 2), [value]);

  const appendWorkflow = () => {
    const now = Date.now();
    onChange([
      ...value,
      {
        id: createDiscountWorkflowId(),
        name: `New workflow ${workflowCount + 1}`,
        thresholdCents: 5000,
        approverRoles: ["manager"],
        escalationEmails: [],
        requireReasonCode: true,
        createdAt: now,
        updatedAt: now,
      },
    ]);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-foreground">Approval workflows</h3>
          <p className="text-sm text-muted-foreground">
            Manage thresholds, approvers, and escalation emails for discount approvals.
          </p>
        </div>
        <Button onClick={appendWorkflow}>Add workflow</Button>
      </div>

      {workflowCount === 0 ? (
        <Card className="border-dashed">
          <CardHeader>
            <CardTitle>No approval workflows</CardTitle>
            <CardDescription>
              Discounts will auto-approve. Add a workflow to introduce reviewers and escalations.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        value.map((workflow, index) => {
          const handleUpdate = (next: Partial<DiscountWorkflowFormValue>) => {
            const updated = value.map((item, idx) =>
              idx === index
                ? {
                    ...item,
                    ...next,
                    updatedAt: Date.now(),
                  }
                : item,
            );
            onChange(updated);
          };

          return (
            <Card key={workflow.id} className="border-border/60 shadow-sm">
              <CardHeader className="flex flex-col gap-2 border-b border-border/60 sm:flex-row sm:items-center sm:justify-between">
                <div className="space-y-1">
                  <CardTitle className="text-lg">{workflow.name}</CardTitle>
                  <CardDescription>Workflow ID: {workflow.id}</CardDescription>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <Badge variant="secondary">{workflow.approverRoles.length} approver role(s)</Badge>
                  <Badge variant="outline">
                    {workflow.requireReasonCode ? "Decline note required" : "Decline note optional"}
                  </Badge>
                  <span>Updated {new Date(workflow.updatedAt).toLocaleString()}</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 p-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-xs font-semibold uppercase text-muted-foreground">Name</label>
                    <Input
                      value={workflow.name}
                      onChange={(event) => handleUpdate({ name: event.target.value })}
                      placeholder="Workflow label"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-semibold uppercase text-muted-foreground">
                      Threshold (cents)
                    </label>
                    <Input
                      inputMode="numeric"
                      value={workflow.thresholdCents.toString()}
                      onChange={(event) =>
                        handleUpdate({
                          thresholdCents: ensureNumber(event.target.value, workflow.thresholdCents, {
                            min: 0,
                            max: 1_000_000,
                          }),
                        })
                      }
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <p className="text-xs font-semibold uppercase text-muted-foreground">Approver roles</p>
                  <div className="flex flex-wrap gap-2">
                    {APPROVER_ROLES.map((role) => (
                      <Button
                        key={role}
                        type="button"
                        variant={workflow.approverRoles.includes(role) ? "default" : "outline"}
                        size="sm"
                        onClick={() =>
                          handleUpdate({
                            approverRoles: toggleArrayValue(workflow.approverRoles, role),
                          })
                        }
                      >
                        {role}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <p className="text-xs font-semibold uppercase text-muted-foreground">Escalation emails</p>
                  <div className="space-y-2">
                    {workflow.escalationEmails.length === 0 ? (
                      <p className="text-xs text-muted-foreground">
                        No escalation emails configured. Add recipients to get notified when approvals stall.
                      </p>
                    ) : (
                      workflow.escalationEmails.map((email, emailIdx) => (
                        <div key={`${workflow.id}-email-${emailIdx}`} className="flex items-center gap-2">
                          <Input
                            value={email}
                            onChange={(event) => {
                              const next = [...workflow.escalationEmails];
                              next[emailIdx] = event.target.value;
                              handleUpdate({ escalationEmails: next });
                            }}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive"
                            onClick={() =>
                              handleUpdate({
                                escalationEmails: workflow.escalationEmails.filter((_, idx) => idx !== emailIdx),
                              })
                            }
                          >
                            Remove
                          </Button>
                        </div>
                      ))
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        handleUpdate({
                          escalationEmails: [...workflow.escalationEmails, ""],
                        })
                      }
                      disabled={workflow.escalationEmails.length >= 10}
                    >
                      Add email
                    </Button>
                  </div>
                </div>

                <div className="flex items-center gap-3 rounded-lg border border-border/60 bg-background px-3 py-2">
                  <Switch
                    id={`workflow-reason-${workflow.id}`}
                    checked={workflow.requireReasonCode}
                    onCheckedChange={(checked) => handleUpdate({ requireReasonCode: checked })}
                  />
                  <label
                    htmlFor={`workflow-reason-${workflow.id}`}
                    className="text-xs font-medium uppercase text-muted-foreground"
                  >
                    Declines require note
                  </label>
                </div>

                <div className="flex justify-between">
                  <span className="text-xs text-muted-foreground">
                    Created {new Date(workflow.createdAt).toLocaleString()}
                  </span>
                  <Button
                    variant="ghost"
                    className="text-destructive"
                    onClick={() => onChange(value.filter((_, idx) => idx !== index))}
                  >
                    Remove workflow
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })
      )}

      <div>
        <h4 className="text-xs font-semibold uppercase text-muted-foreground">JSON preview</h4>
        <pre className="mt-2 max-h-64 overflow-auto rounded-lg border border-border/60 bg-muted/20 p-4 text-xs text-muted-foreground">
          {preview}
        </pre>
      </div>
    </div>
  );
};
